import { useCallback, useEffect, useMemo, useRef } from 'react'
import { VariableSizeTree as Tree } from 'react-vtree'
import Styles from './ListTree.module.scss'
import { ListRow } from './ListRow'
import { AddRowInline } from '../../FileTree/components/AddRowInline/AddRowInline'
import { ListTreeNodeData, ListTreeProps } from './Types'
import { useFinderContext } from 'screens/Finder/FinderContext'
import { DragDropContext, Droppable, DroppableProvided } from 'react-beautiful-dnd'
import RelativePortal from 'react-relative-portal'
import { pluralize } from 'utils'
import { FetchRequestState, AlmanacFile as File, ViewType } from 'types'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { ListRowWrapper } from './ListRowWrapper'
import { Can } from 'components/Can'
import { SubjectType } from 'utils/caslAbility'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { FloatingButton } from 'blaze-ui/FloatingButton/FloatingButton'
import { Plus1 } from 'blaze-ui/icons/24'
import { WizardsDrawer } from 'components/Wizards/WizardsDrawer/WizardsDrawer'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { UploadButton } from 'screens/blaze/Home/components/UploadButton/UploadButton'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { LoadingRows } from 'screens/RecentDocsContainer/components/FilesTable/LoadingRows'

export const ROW_HEIGHT = 48

const getNodeData = (options: {
  node: File
  nestingLevel: number
  isLast: boolean
  enablePagination?: boolean
  defaultHeight: number
}) => {
  const { node, nestingLevel, isLast, enablePagination, defaultHeight } = options

  let data: ListTreeNodeData = {
    id: `${node.id}-${node.mention_id || nestingLevel}`,
    file: node,
    isOpenByDefault: false,
    nestingLevel,
    isLast,
    defaultHeight,
    enablePagination,
  }

  return {
    data,
    nestingLevel,
    node,
  }
}

const ListTree = observer(({ files, inlineAddPropertyValues, enablePagination }: ListTreeProps) => {
  const { drawersStore } = useRootStore()
  const {
    handleDragEnd,
    handleDragStart,
    handleDragUpdate,
    selectedFiles,
    hasMore,
    resetSelection,
    openFolderIds,
    loadingState,
    filesView,
  } = useFinderContext()
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProject = useUploadsProject(workspaceId)?.data?.project
  const { currentFolder } = useCurrentFolderStore()
  const { isMobile } = useResponsiveDesign()
  const selfRef = useRef<HTMLDivElement>(null)

  const isUploadsProject = uploadsProject && uploadsProject.id === filesView?.schema.project?.key

  const treeWalker = useCallback(
    function* treeWalker() {
      for (let i = 0; i < files.length; i++) {
        yield getNodeData({
          node: files[i],
          nestingLevel: 0,
          isLast: i === files.length - 1,
          enablePagination,
          defaultHeight: ROW_HEIGHT,
        })
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      while (true) {
        const parentMeta: any = yield

        // recents, mine, shared and imported aren't expandable so this isn't needed
        if (currentFolder) {
          for (let i = 0; i < parentMeta.node.children?.length; i++) {
            yield getNodeData({
              node: parentMeta.node.children[i],
              nestingLevel: parentMeta.nestingLevel + 1,
              isLast: i === parentMeta.node.children.length - 1,
              enablePagination,
              defaultHeight: ROW_HEIGHT,
            })
          }
        }
      }
    },
    [files, currentFolder, enablePagination],
  )

  const countVisibleFiles = useCallback(
    (files: File[]) => {
      let count = 0

      files.forEach((file) => {
        if (file.children && currentFolder && openFolderIds.includes(file.id)) {
          count += countVisibleFiles(file.children)
        }

        count += 1
      })

      return count
    },
    [currentFolder, openFolderIds],
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        resetSelection()
      }
    }

    window.addEventListener('keydown', onKeyDown, true)

    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [resetSelection])

  const fileSystemHeight = useMemo(
    () => (!!files ? ROW_HEIGHT * countVisibleFiles(files) : 0),
    [countVisibleFiles, files],
  )

  return (
    <>
      {!!files?.length && (
        <>
          <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart} onDragUpdate={handleDragUpdate}>
            <Droppable
              droppableId="droppable"
              mode="virtual"
              renderClone={(provided) => {
                const previewFiles = selectedFiles.slice(0, 2)
                const restCount = selectedFiles.length - previewFiles.length
                return (
                  <div
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    ref={provided.innerRef}
                    className={Styles.draggedFiles}
                  >
                    <div className={Styles.dragCount}>{selectedFiles.length}</div>
                    <RelativePortal component="div" left={64} top={0}>
                      <div className={Styles.draggedFiles}>
                        {previewFiles.map((file) => (
                          <div key={file.id} className={Styles.draggedFile}>
                            <span>{file.title}</span>
                          </div>
                        ))}
                        {restCount > 0 && (
                          <div className={Styles.draggedFile}>
                            {restCount} other {pluralize('file', restCount)}
                          </div>
                        )}
                      </div>
                    </RelativePortal>
                  </div>
                )
              }}
            >
              {(droppableProvided: DroppableProvided) => (
                <div {...droppableProvided.droppableProps} ref={selfRef}>
                  {/* @ts-ignore */}
                  <Tree
                    async
                    treeWalker={treeWalker}
                    height={fileSystemHeight}
                    rowComponent={ListRowWrapper}
                    innerRef={droppableProvided.innerRef}
                    className={Styles.tree}
                  >
                    {/* TODO: fix typing here. The tree library complains about index not being a prop, but it's guaranteed to be there by the rowComponent above */}
                    {ListRow as any}
                  </Tree>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </>
      )}
      {hasMore && enablePagination && loadingState === FetchRequestState.Loading && (
        <div className={Styles.loadingRows}>
          <LoadingRows
            count={3}
            mainLoadingCellWidth={isMobile ? 'calc(100% - 40px)' : undefined}
            loadingCellWidth={160}
            extraColumns={isMobile ? 0 : 1}
          />
        </div>
      )}
      {!(hasMore && enablePagination) && currentFolder?.has_edit_access && !isMobile && (
        <Can I={'create'} a={SubjectType.ALMANAC_FILE} inWorkspace={currentFolder.real_workspace_id}>
          <AddRowInline viewType={ViewType.List} withPropertyValues={inlineAddPropertyValues} />
        </Can>
      )}
      {isMobile && (
        <>
          {isUploadsProject ? (
            <div className={Styles.uploadMoreButtonWrapper}>
              <div className={Styles.uploadMoreButton}>
                <UploadButton label="Upload More" variant="primary" fullWidth size="xl" />
              </div>
            </div>
          ) : (
            <FloatingButton
              onPress={() => {
                drawersStore.openDrawer(WizardsDrawer, { initialValues: { project: currentFolder } })
              }}
              icon={Plus1}
            />
          )}
        </>
      )}
    </>
  )
})

export { ListTree }
